import { useState, useEffect } from "react";
import { makeRequestAuth } from "../../_config/api";
import { toast } from "react-toastify";
import { X, Save, Plus, Trash2, CreditCard, Landmark } from "lucide-react";

function UpdateSellOrderModal({ isOpen, onClose, data, refresh }) {
  const [form, setForm] = useState({
    name: "",
    rate: "",
    amount: "",
    walletType: "USDT",
    status: "pending",
    paymentMethods: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (data) {
      setForm({
        name: data.name || "",
        rate: data.rate || "",
        amount: data.amount || "",
        walletType: data.walletType || "USDT",
        status: data.status || "pending",
        paymentMethods: data.paymentMethods || [
          {
            methodType: "UPI",
            label: "",
            isPrimary: true,
            details: { upiId: "" },
          },
        ],
      });
    }
  }, [data]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePaymentMethodChange = (index, field, value, isDetail = false) => {
    const updatedMethods = [...form.paymentMethods];
    if (isDetail) {
      updatedMethods[index].details = {
        ...updatedMethods[index].details,
        [field]: value,
      };
    } else {
      updatedMethods[index][field] = value;
      // Reset details when switching type
      if (field === "methodType") {
        updatedMethods[index].details =
          value === "UPI"
            ? { upiId: "" }
            : {
                accountHolderName: "",
                bankName: "",
                accountNumber: "",
                ifsc: "",
                branchName: "",
              };
      }
    }
    setForm({ ...form, paymentMethods: updatedMethods });
  };

  const addMethod = () => {
    setForm({
      ...form,
      paymentMethods: [
        ...form.paymentMethods,
        { methodType: "UPI", label: "", details: { upiId: "" } },
      ],
    });
  };

  const removeMethod = (index) => {
    if (form.paymentMethods.length <= 1)
      return toast.error("At least one method required");
    setForm({
      ...form,
      paymentMethods: form.paymentMethods.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const cleanedMethods = form.paymentMethods.map((m, i) => {
        const cleanDetails = {};

        Object.entries(m.details || {}).forEach(([key, value]) => {
          if (value !== "") {
            cleanDetails[key] = value;
          }
        });

        return {
          methodType: m.methodType,
          label: m.label || "",
          isPrimary: m.isPrimary ?? i === 0,
          details: cleanDetails,
        };
      });

      const payload = {
        id: data._id,
        name: form.name,
        rate: Number(form.rate),
        amount: Number(form.amount),
        walletType: form.walletType,
        status: form.status,
        paymentMethods: cleanedMethods,
      };

      await makeRequestAuth("post", "/admin/updateP2PSellOrder", payload);

      toast.success("Sell order updated!");
      refresh();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm transition-all">
      <div className="w-full max-w-2xl bg-white dark:bg-[#121212] rounded-t-3xl sm:rounded-2xl shadow-2xl border border-gray-200 dark:border-zinc-800 overflow-hidden transform transition-all animate-in fade-in slide-in-from-bottom-10 duration-300">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-zinc-800 bg-white dark:bg-[#121212] sticky top-0 z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <CreditCard className="text-purple-500" size={22} />
              Update Sell Order
            </h2>
            <p className="text-[10px] text-gray-500 font-mono mt-0.5 uppercase tracking-tighter">
              REF: {data?._id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-gray-100 dark:bg-zinc-800 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 rounded-full transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form Body */}
        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-6 custom-scrollbar dark:text-white">
          {/* Section 1: Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1 space-y-1.5">
              <label className="text-[11px] font-bold text-gray-400 uppercase ml-1">
                Seller Name
              </label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900 focus:ring-2 focus:ring-purple-500 outline-none transition-all"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-400 uppercase ml-1">
                Rate
              </label>
              <input
                name="rate"
                type="number"
                value={form.rate}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900 focus:ring-2 focus:ring-purple-500 outline-none transition-all"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-400 uppercase ml-1">
                Amount
              </label>
              <input
                name="amount"
                type="number"
                value={form.amount}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900 focus:ring-2 focus:ring-purple-500 outline-none transition-all"
              />
            </div>
          </div>

          <hr className="border-gray-100 dark:border-zinc-800" />

          {/* Section 2: Payment Methods */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-purple-600 dark:text-purple-400 uppercase tracking-widest flex items-center gap-2">
                <Landmark size={16} /> Payment Methods
              </h3>
              <button
                onClick={addMethod}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 text-purple-600 dark:text-purple-400 text-xs font-bold rounded-lg hover:bg-purple-500 hover:text-white transition-all"
              >
                <Plus size={14} /> Add New
              </button>
            </div>

            {form.paymentMethods.map((method, index) => (
              <div
                key={index}
                className="relative p-5 rounded-2xl border border-gray-200 dark:border-zinc-800 bg-gray-50/50 dark:bg-zinc-900/30 space-y-4 animate-in slide-in-from-right-5"
              >
                <button
                  onClick={() => removeMethod(index)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 size={16} />
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Method Type */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase ml-1">
                      Method Type
                    </label>
                    <select
                      value={method.methodType}
                      onChange={(e) =>
                        handlePaymentMethodChange(
                          index,
                          "methodType",
                          e.target.value,
                        )
                      }
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-sm outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="UPI">UPI</option>
                      <option value="BANK_TRANSFER">Bank Transfer</option>
                    </select>
                  </div>

                  {/* Holder Name / Label */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase ml-1">
                      Holder Name
                    </label>
                    <input
                      value={method.label}
                      onChange={(e) =>
                        handlePaymentMethodChange(
                          index,
                          "label",
                          e.target.value,
                        )
                      }
                      placeholder="Holder Name"
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-sm outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Dynamic Fields */}
                  {method.methodType === "UPI" ? (
                    <div className="md:col-span-2 space-y-1">
                      <label className="text-[10px] font-bold text-gray-500 uppercase ml-1">
                        UPI ID
                      </label>
                      <input
                        value={method.details.upiId || ""}
                        onChange={(e) =>
                          handlePaymentMethodChange(
                            index,
                            "upiId",
                            e.target.value,
                            true,
                          )
                        }
                        placeholder="upiId"
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-sm outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gray-500 uppercase ml-1">
                          Bank Name
                        </label>
                        <input
                          value={method.details.bankName || ""}
                          onChange={(e) =>
                            handlePaymentMethodChange(
                              index,
                              "bankName",
                              e.target.value,
                              true,
                            )
                          }
                          placeholder="Bank Name"
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-sm outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gray-500 uppercase ml-1">
                          Account Number
                        </label>
                        <input
                          value={method.details.accountNumber || ""}
                          onChange={(e) =>
                            handlePaymentMethodChange(
                              index,
                              "accountNumber",
                              e.target.value,
                              true,
                            )
                          }
                          placeholder="Account Number"
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-sm outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gray-500 uppercase ml-1">
                          IFSC Code
                        </label>
                        <input
                          value={method.details.ifsc || ""}
                          onChange={(e) =>
                            handlePaymentMethodChange(
                              index,
                              "ifsc",
                              e.target.value,
                              true,
                            )
                          }
                          placeholder="IFSC Code"
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-sm outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gray-500 uppercase ml-1">
                          Branch Name
                        </label>
                        <input
                          value={method.details.branchName || ""}
                          onChange={(e) =>
                            handlePaymentMethodChange(
                              index,
                              "branchName",
                              e.target.value,
                              true,
                            )
                          }
                          placeholder="Branch Name"
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-sm outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Section 3: Status & Wallet */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-400 uppercase ml-1">
                Wallet Type
              </label>
              <select
                name="walletType"
                value={form.walletType}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900 outline-none"
              >
                <option value="USDT">USDT</option>
                <option value="TOKEN">TOKEN</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-400 uppercase ml-1">
                Status
              </label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900 font-bold outline-none ${form.status === "approve" ? "text-green-500" : form.status === "reject" ? "text-red-500" : "text-blue-500"}`}
              >
                <option value="draft">📝 Draft</option>
                <option value="pending">⏳ Pending</option>
                <option value="requested">📩 Requested</option>
                <option value="process">⚙️ In Process</option>
                <option value="approve">✅ Approved</option>
                <option value="reject">❌ Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row gap-3 px-6 py-6 bg-gray-50 dark:bg-zinc-900/50 border-t border-gray-100 dark:border-zinc-800">
          <button
            onClick={onClose}
            className="order-2 sm:order-1 flex-1 px-6 py-3 text-sm font-bold text-gray-500 hover:bg-gray-200 dark:hover:bg-zinc-800 rounded-xl transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="order-1 sm:order-2 flex-[2] flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 active:scale-95 rounded-xl shadow-xl shadow-purple-500/20 transition-all disabled:opacity-50"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Save size={18} /> Update Sell Order
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UpdateSellOrderModal;
